import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  WarningIcon,
  FileXlsIcon,
  SpinnerIcon,
  ArrowSquareOutIcon,
  ArrowLeftIcon,
  CopyIcon,
  TrashIcon,
  CheckCircleIcon,
  CalendarIcon,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import {
  OVERLAP_RESOLUTIONS,
  type OverlappingRebateTransactionRecord,
  type OverlapResolution,
} from '@erp/shared-types';
import { T, useGT, Var, Num, Currency } from 'gt-react';

type WizardStep = 'selection' | 'confirmation';

const OVERLAP_DATE_LOCALE = 'en-US';
const OVERLAP_DATE_UTC_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
};
const OVERLAP_DATE_LOCAL_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};
const OVERLAP_CURRENCY_FORMAT_OPTIONS: Intl.NumberFormatOptions = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
};

interface OverlapWarningModalProps {
  open: boolean;
  onClose: () => void;
  onResolve: (resolution: OverlapResolution) => void;
  overlappingTransactionRecords: OverlappingRebateTransactionRecord[];
  totalAffectedLineItems: number;
  isSubmitting?: boolean;
  supplierId?: string;
}

function formatOverlapDate(dateStr: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toLocaleDateString(
      OVERLAP_DATE_LOCALE,
      OVERLAP_DATE_UTC_OPTIONS,
    );
  }
  const date = new Date(dateStr);
  return date.toLocaleDateString(
    OVERLAP_DATE_LOCALE,
    OVERLAP_DATE_LOCAL_OPTIONS,
  );
}

/**
 * Modal that displays overlapping invoices and lets the user choose how to handle them
 */
function OverlapWarningModal({
  open,
  onClose,
  onResolve,
  overlappingTransactionRecords,
  totalAffectedLineItems,
  isSubmitting = false,
  supplierId,
}: OverlapWarningModalProps) {
  const gt = useGT();
  const [step, setStep] = useState<WizardStep>('selection');
  const [selectedResolution, setSelectedResolution] =
    useState<OverlapResolution | null>(null);

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-US', OVERLAP_CURRENCY_FORMAT_OPTIONS),
    [],
  );

  const formatCurrency = (value: number) => currencyFormatter.format(value);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('selection');
      setSelectedResolution(null);
    }
  }, [open]);

  const resolutionUiConfig = useMemo<
    Record<
      OverlapResolution,
      {
        bgColor: string;
        icon: React.ReactNode;
        title: string;
        confirmLabel: string;
        confirmVariant: 'default' | 'destructive';
      }
    >
  >(
    () => ({
      [OVERLAP_RESOLUTIONS.KEEP_BOTH]: {
        bgColor: 'bg-blue-100',
        icon: <CopyIcon className="h-5 w-5 text-blue-600" />,
        title: gt('Confirm: Keep Both', {
          context: 'rebate overlap modal: confirm step title for keep both',
        }),
        confirmLabel: gt('Confirm: Keep Both', {
          context: 'rebate overlap modal: confirm button for keep both',
        }),
        confirmVariant: 'default',
      },
      [OVERLAP_RESOLUTIONS.PARTIAL_OVERRIDE]: {
        bgColor: 'bg-blue-100',
        icon: <CalendarIcon className="h-5 w-5 text-blue-600" />,
        title: gt('Confirm: Replace Overlapping Months', {
          context:
            'rebate overlap modal: confirm step title for partial replace',
        }),
        confirmLabel: gt('Confirm: Replace Overlapping Months', {
          context: 'rebate overlap modal: confirm button for partial replace',
        }),
        confirmVariant: 'default',
      },
      [OVERLAP_RESOLUTIONS.OVERRIDE]: {
        bgColor: 'bg-red-100',
        icon: <TrashIcon className="h-5 w-5 text-red-600" />,
        title: gt('Confirm: Replace Existing', {
          context: 'rebate overlap modal: confirm step title for full replace',
        }),
        confirmLabel: gt('Confirm: Replace Existing', {
          context: 'rebate overlap modal: confirm button for full replace',
        }),
        confirmVariant: 'destructive',
      },
    }),
    [gt],
  );

  const handleResolutionSelect = (resolution: OverlapResolution) => {
    setSelectedResolution(resolution);
    setStep('confirmation');
  };

  const handleBack = () => {
    setStep('selection');
    setSelectedResolution(null);
  };

  const handleConfirm = () => {
    if (selectedResolution) {
      onResolve(selectedResolution);
    }
  };

  const totalRemovedValue = useMemo(
    () =>
      overlappingTransactionRecords.reduce(
        (sum, inv) => sum + inv.totalValue,
        0,
      ),
    [overlappingTransactionRecords],
  );

  const renderSelectionStep = () => (
    <>
      <AlertDialogHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <WarningIcon className="h-5 w-5 text-amber-600" />
          </div>
          <AlertDialogTitle className="text-xl">
            <T context="rebate overlap modal: dialog title when overlap detected">
              Overlapping Data Detected
            </T>
          </AlertDialogTitle>
        </div>
        <AlertDialogDescription className="mt-2 text-base text-gray-700">
          <T context="rebate overlap modal: dialog description explaining overlap">
            Your submission contains dates that overlap with existing data.
            Please choose how to proceed.
          </T>
        </AlertDialogDescription>
      </AlertDialogHeader>

      {/* Summary */}
      <div className="my-4 rounded-lg bg-amber-50 p-4 border border-amber-200">
        <div className="flex items-center justify-between">
          <p className="text-sm text-amber-900">
            <T context="rebate overlap modal: summary of overlapping submissions">
              Found{' '}
              <strong>
                <Num>{overlappingTransactionRecords.length}</Num>
              </strong>{' '}
              existing submission(s) with{' '}
              <strong>
                <Num>{totalAffectedLineItems}</Num>
              </strong>{' '}
              total line items that overlap with your new data.
            </T>
          </p>
          {supplierId && (
            <Link
              to="/rebates/supplier-reported-amounts/$supplierId"
              params={{ supplierId }}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="sm" className="gap-1">
                <ArrowSquareOutIcon className="h-3.5 w-3.5" />
                <T context="rebate overlap modal: link to supplier reported amounts">
                  View All Reported Amounts
                </T>
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Overlapping Invoices List */}
      <div className="max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-50">
            <tr className="border-b">
              <th className="py-2 px-3 text-left font-medium text-gray-600">
                <T context="rebate overlap modal: overlapping table column header">
                  ID
                </T>
              </th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">
                <T context="rebate overlap modal: overlapping table column header">
                  File
                </T>
              </th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">
                <T context="rebate overlap modal: overlapping table column header">
                  Date Range
                </T>
              </th>
              <th className="py-2 px-3 text-right font-medium text-gray-600">
                <T context="rebate overlap modal: overlapping table column header">
                  Line Items
                </T>
              </th>
              <th className="py-2 px-3 text-right font-medium text-gray-600">
                <T context="rebate overlap modal: overlapping table column header">
                  Total Value
                </T>
              </th>
              <th className="py-2 px-3 text-left font-medium text-gray-600">
                <T context="rebate overlap modal: overlapping table column header">
                  Submitted
                </T>
              </th>
            </tr>
          </thead>
          <tbody>
            {overlappingTransactionRecords.map((invoice) => (
              <tr
                key={invoice.id}
                className="border-b border-gray-100 hover:bg-gray-50"
              >
                <td className="py-2 px-3">
                  <div className="font-medium text-gray-900">
                    {invoice.invoiceNumber}
                  </div>
                </td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    <FileXlsIcon className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900">
                        {invoice.filename}
                      </div>
                      {invoice.sheetName && (
                        <div className="text-xs text-gray-500">
                          <T context="rebate overlap modal: spreadsheet sheet name label">
                            Sheet: <Var>{invoice.sheetName}</Var>
                          </T>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-2 px-3 text-gray-700">
                  {formatOverlapDate(invoice.dateRange.start)} -{' '}
                  {formatOverlapDate(invoice.dateRange.end)}
                </td>
                <td className="py-2 px-3 text-right text-gray-700">
                  {invoice.lineItemCount.toLocaleString()}
                </td>
                <td className="py-2 px-3 text-right text-gray-700">
                  {formatCurrency(invoice.totalValue)}
                </td>
                <td className="py-2 px-3 text-gray-500 text-xs">
                  {formatOverlapDate(invoice.submittedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialogFooter className="mt-6 flex-col gap-3 sm:flex-row">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={isSubmitting}
          className="w-full sm:w-auto"
        >
          <T context="rebate overlap modal: cancel overlap resolution">
            Cancel
          </T>
        </Button>
        <Button
          variant="secondary"
          onClick={() => handleResolutionSelect(OVERLAP_RESOLUTIONS.KEEP_BOTH)}
          disabled={isSubmitting}
          className="w-full sm:w-auto"
        >
          <T context="rebate overlap modal: resolution option keep both">
            Keep Both
          </T>
        </Button>
        <Button
          variant="default"
          onClick={() =>
            handleResolutionSelect(OVERLAP_RESOLUTIONS.PARTIAL_OVERRIDE)
          }
          disabled={isSubmitting}
          className="w-full sm:w-auto"
        >
          <T context="rebate overlap modal: resolution option partial override">
            Replace Overlapping Months
          </T>
        </Button>
        <Button
          variant="destructive"
          onClick={() => handleResolutionSelect(OVERLAP_RESOLUTIONS.OVERRIDE)}
          disabled={isSubmitting}
          className="w-full sm:w-auto"
        >
          <T context="rebate overlap modal: resolution option full override">
            Replace Existing
          </T>
        </Button>
      </AlertDialogFooter>

      {/* Help text */}
      <div className="mt-4 text-xs text-gray-500 space-y-1">
        <p>
          <T context="rebate overlap modal: help text for replace existing option">
            <strong>Replace Existing:</strong> Deletes the overlapping
            submissions and replaces them with your new data.
          </T>
        </p>
        <p>
          <T context="rebate overlap modal: help text for partial replace option">
            <strong>Replace Overlapping Months:</strong> Deactivates only line
            items for months present in your new upload. Other months remain
            unchanged.
          </T>
        </p>
        <p>
          <T context="rebate overlap modal: help text for keep both option">
            <strong>Keep Both:</strong> Adds your new data alongside the
            existing data. This may cause duplicates.
          </T>
        </p>
      </div>
    </>
  );

  const renderConfirmationStep = () => {
    const resolution = selectedResolution ?? OVERLAP_RESOLUTIONS.OVERRIDE;
    const { bgColor, icon, title, confirmLabel, confirmVariant } =
      resolutionUiConfig[resolution];
    const isKeepBoth = resolution === OVERLAP_RESOLUTIONS.KEEP_BOTH;
    const isPartialOverride =
      resolution === OVERLAP_RESOLUTIONS.PARTIAL_OVERRIDE;

    return (
      <>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full ${bgColor}`}
            >
              {icon}
            </div>
            <AlertDialogTitle className="text-xl">{title}</AlertDialogTitle>
          </div>
        </AlertDialogHeader>

        <div className="my-4 space-y-4">
          {isKeepBoth ? (
            <>
              <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">
                  <T context="rebate overlap modal: section heading what will happen">
                    What will happen:
                  </T>
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: keep both bullet new submission">
                        Your new data will be added to the system as a new
                        submission
                      </T>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: keep both bullet existing unchanged">
                        The existing{' '}
                        <Num>{overlappingTransactionRecords.length}</Num>{' '}
                        submission(s) with{' '}
                        <Num>{totalAffectedLineItems.toLocaleString()}</Num>{' '}
                        line items will remain unchanged
                      </T>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: keep both bullet visibility">
                        Both old and new data will be visible in reports and
                        analytics
                      </T>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                <h4 className="font-semibold text-red-900 mb-2">
                  <T context="rebate overlap modal: important considerations heading">
                    Important considerations:
                  </T>
                </h4>
                <ul className="space-y-2 text-sm text-red-800">
                  <li className="flex items-start gap-2">
                    <WarningIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: duplicate data warning">
                        This may result in <strong>duplicate data</strong> for
                        the overlapping date ranges
                      </T>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <WarningIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: inflated totals warning">
                        Totals and aggregations may be inflated due to duplicate
                        entries
                      </T>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <WarningIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: use only when certain warning">
                        Use this option only if you are certain the new data
                        represents additional transactions
                      </T>
                    </span>
                  </li>
                </ul>
              </div>
            </>
          ) : isPartialOverride ? (
            <>
              <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">
                  <T context="rebate overlap modal: partial override what will happen heading">
                    What will happen:
                  </T>
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: partial override bullet deactivate months">
                        Only line items for <strong>months present</strong> in
                        your new upload will be deactivated
                      </T>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: partial override bullet other months">
                        Line items for all other months will remain{' '}
                        <strong>unchanged and active</strong>
                      </T>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: partial override bullet audit trail">
                        Original submissions are preserved as an audit trail
                        (not deleted)
                      </T>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: partial override bullet new submission">
                        Your new data will be added as a new submission
                      </T>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
                <p className="text-sm text-gray-700">
                  <T context="rebate overlap modal: partial override safest option note">
                    This is the safest option when you need to correct data for
                    specific months without affecting the rest of your
                    submissions.
                  </T>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                <h4 className="font-semibold text-red-900 mb-2">
                  <T context="rebate overlap modal: full replace what will happen heading">
                    What will happen:
                  </T>
                </h4>
                <ul className="space-y-2 text-sm text-red-800">
                  <li className="flex items-start gap-2">
                    <TrashIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: full replace bullet delete submissions">
                        The existing{' '}
                        <Num>{overlappingTransactionRecords.length}</Num>{' '}
                        submission(s) will be{' '}
                        <strong>permanently deleted</strong>
                      </T>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <TrashIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: full replace bullet line items removed">
                        <strong>
                          <Num>{totalAffectedLineItems.toLocaleString()}</Num>
                        </strong>{' '}
                        line items totaling{' '}
                        <strong>
                          <Currency>{totalRemovedValue}</Currency>
                        </strong>{' '}
                        will be removed
                      </T>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <T context="rebate overlap modal: full replace bullet replace deleted">
                        Your new data will replace the deleted submissions
                      </T>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  <T context="rebate overlap modal: list heading submissions to delete">
                    Submissions to be deleted:
                  </T>
                </h4>
                <div className="max-h-32 overflow-y-auto">
                  <ul className="space-y-1 text-sm text-gray-700">
                    {overlappingTransactionRecords.map((invoice) => (
                      <li key={invoice.id} className="flex items-center gap-2">
                        <FileXlsIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900">
                          {invoice.invoiceNumber}
                        </span>
                        <span className="text-gray-400">-</span>
                        <span className="truncate text-gray-600">
                          {invoice.filename}
                        </span>
                        <span className="text-gray-400">-</span>
                        <span className="text-gray-500 text-xs">
                          <T context="rebate overlap modal: submission summary line item count and value">
                            <Num>{invoice.lineItemCount}</Num> items,{' '}
                            <Currency>{invoice.totalValue}</Currency>
                          </T>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="rounded-lg bg-amber-50 p-4 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <T context="rebate overlap modal: irreversible action warning">
                    <strong>This action cannot be undone.</strong> Make sure you
                    have verified that your new data should replace the existing
                    submissions.
                  </T>
                </p>
              </div>
            </>
          )}
        </div>

        <AlertDialogFooter className="mt-6 flex-col gap-3 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={isSubmitting}
            className="w-full sm:w-auto gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <T context="rebate overlap modal: back button from confirm step">
              Go Back
            </T>
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting && (
              <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </>
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent className="z-[1500] max-w-2xl">
        {step === 'selection'
          ? renderSelectionStep()
          : renderConfirmationStep()}
      </AlertDialogContent>
    </AlertDialog>
  );
}

export { OverlapWarningModal };
